import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTab } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { State, Store } from '@ngrx/store';
import {
  CustomerServiceNote,
  Driver,
  MobilityProfileDetail,
  OrganizationRiderDetail,
  Payment,
  PaymentAccount,
  Rider,
  PersonSuspensionDetail,
  Trip
} from '@uzurv/clara';
import { isNullOrUndefined, PermissionsConstants } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, combineLatest, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { filter, first, map, startWith, tap } from 'rxjs/operators';
import { OrganizationRiderPaymentAccountDialog } from '../../components/dialogs/payment-account-dialog/organization-rider/organization-rider-payment-account-dialog.dialog';
import * as OrganizationRiderActions from '../../store/organization-rider/organization-rider.actions';
import { OrganizationRiderState } from '../../store/organization-rider/organization-rider.reducer';
import { Tab } from '../../services/tab/tab.service';
import { Tabbing } from '../tabbing/tabbing.page';
import { TabsState } from '../../store/tab/tab.reducer';
import * as OrganizationRiderSelectors from '../../store/organization-rider/organization-rider.selectors';
import { OpenTripDetailTab } from '../../store/trip-detail/trip-detail.actions';

export enum RiderDetailTabs {
  BASIC_INFO = 'Basic Info',
  RIDER_MOBILITY_PROFILE = 'Mobility Profile',
  RIDES = 'Rides',
  INTERNAL_NOTES = 'Internal Notes',
  FAVORITE_DRIVERS = 'Favorite Drivers',
  IGNORED_DRIVERS = 'Ignored Drivers',
  MANAGE_PAYMENT_ACCOUNTS = 'Manage Payment Accounts',
  PAYMENTS = 'Payments',
  SUSPENSIONS = 'Suspensions'
}

@Component({
  selector: 'app-rider-details',
  templateUrl: './organization-rider-detail.page.html',
  styleUrls: ['./organization-rider-detail.page.scss']
})
export class OrganizationRiderDetailPage extends Tabbing<OrganizationRiderState> implements OnInit {
  readonly permissionConstants = PermissionsConstants;
  readonly RiderDetailTabs = RiderDetailTabs;

  @ViewChild('eligibilityToggle') eligibilityToggle: MatSlideToggle;
  @ViewChildren(MatTab) tabs: QueryList<MatTab>;

  organizationRiderDetail$ = this.store$.select(OrganizationRiderSelectors.getRider);
  organizationRiderMobilityProfile$ = this.store$.select(OrganizationRiderSelectors.getRiderMobilityProfile);
  organizationRiderAddress$ = this.store$.select(OrganizationRiderSelectors.getOrganizationRiderAddress);
  riderPaymentAccounts$ = this.store$.select(OrganizationRiderSelectors.getRiderPaymentAccounts);
  ignoredDrivers$ = this.store$.select(OrganizationRiderSelectors.getIgnoredDrivers);
  outstandingCharges$ = this.store$.select(OrganizationRiderSelectors.getOutstandingCharges);
  payments$ = this.store$.select(OrganizationRiderSelectors.getPayments);
  expandedPayment$ = this.store$.select(OrganizationRiderSelectors.getExpandedPayment);
  expandedPaymentTransactions$ = this.store$.select(OrganizationRiderSelectors.getExpandedPaymentTransactions);
  loadingTransactions$ = this.store$.select(OrganizationRiderSelectors.getLoadingTransactions);
  isOrganizationRiderAppEnabled$ = this.store$.select(OrganizationRiderSelectors.isOrganizationRiderAppEnabled);
  isSendingUzurvRideAppInvitation$ = this.store$.select(OrganizationRiderSelectors.isSendingUzurvRideAppInvitation);
  customerServiceNotes$ = this.store$.select(OrganizationRiderSelectors.getCustomerServiceNotes);
  uzurvRiderProfile$ = this.store$.select(OrganizationRiderSelectors.getUzurvRiderProfile);
  riderProfileDetail$ = this.store$.select(OrganizationRiderSelectors.getRiderProfileDetail);
  organizationRiderDetails$ = this.store$.select(OrganizationRiderSelectors.getOrganizationRiderDetails);
  isLoadingRiderDetails$ = this.store$.select(OrganizationRiderSelectors.isLoadingRiderDetails);
  isPerformingRiderSuspensionAction$ = this.store$.select(OrganizationRiderSelectors.isPerformingRiderSuspensionAction);
  isRiderSuspended$ = this.store$.select(OrganizationRiderSelectors.isRiderSuspended);
  hasActiveSuspensions$ = this.store$.select(OrganizationRiderSelectors.hasActiveSuspensions);
  getUzurvRiderFullName$ = this.store$.select(OrganizationRiderSelectors.getUzurvRiderFullName);
  getFavoriteDrivers$ = this.store$.select(OrganizationRiderSelectors.getFavoriteDrivers);
  riderTrips$ = this.store$.select(OrganizationRiderSelectors.getRiderTrips);
  selectedRiderDetailTab$ = this.store$.select(OrganizationRiderSelectors.getSelectedRiderDetailTab);
  tripsPageIndex$ = this.store$.select(OrganizationRiderSelectors.getTripsPageIndex);
  tripsPageSize$ = this.store$.select(OrganizationRiderSelectors.getTripsPageSize);
  shouldShowMobilityProfile$ = this.store$.select(OrganizationRiderSelectors.getShouldShowMobilityProfile);
  shouldDisableMobilityProfileSelection$ = this.store$.select(OrganizationRiderSelectors.getShouldDisableMobilityProfileSelection);
  shouldDisableMobilityProfileSubmitButtons$ = this.store$.select(OrganizationRiderSelectors.getShouldDisableMobilityProfileSubmitButtons);
  getRiderSuspensions$ = this.store$.select(OrganizationRiderSelectors.getRiderSuspensions);
  riderNotFound$ = this.store$.select(OrganizationRiderSelectors.getRiderNotFound);
  riderIneligibleForOrganization$ = this.store$.select(OrganizationRiderSelectors.getRiderIneligibleForOrganization);
  showPaymentTabs$ = this.store$.select(OrganizationRiderSelectors.getShowPaymentTabs);
  selectedTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  rider: Rider;

  constructor(
    protected readonly tabStore$: Store<TabsState>,
    private store$: Store<State<OrganizationRiderState>>,
    private authDataService: AuthDataService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private actions$: Actions
  ) {
    super(tabStore$);
  }

  ngOnInit() {
    this.store$.dispatch(OrganizationRiderActions.SetNewItineraryStarted({ isNewItinerary: false }));

    this.actions$
      .pipe(
        ofType(OrganizationRiderActions.GetRiderProfileSuccess),
        map(action => `${action.riderProfile?.firstName} ${action.riderProfile?.lastName[0]}.`),
        first(),
        tap(tabName => this.updateTabName(`Rider: ${tabName}`))
      )
      .subscribe();

    this.uzurvRiderProfile$.pipe(takeUntil(this.destroyed$)).subscribe(rider => {
      this.rider = rider;
    });
  }

  ngAfterViewInit() {
    // Map selected rider detail tab to a tab index
    combineLatest([this.selectedRiderDetailTab$, this.tabs.changes.pipe(startWith(this.tabs))])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([selectedRiderDetailTab]) => {
        const tabIndex = this.tabs.toArray().findIndex(tab => tab.textLabel === selectedRiderDetailTab);
        if (tabIndex !== -1) {
          // Using a microtask to avoid ExpressionChangedAfterItHasBeenCheckedError
          queueMicrotask(() => this.selectedTabIndex$.next(tabIndex));
        }
      });
    // Run navigateToInitialTab() once when organizationRiderDetails are emitted.
    this.organizationRiderDetail$
      .pipe(
        filter(organizationRiderDetails => !isNullOrUndefined(organizationRiderDetails)),
        first()
      )
      .subscribe(() => {
        this.navigateToInitialTab();
      });
  }

  ngOnDestroy() {
    this.selectedTabIndex$.complete();

    super.ngOnDestroy();
  }

  navigateToInitialTab() {
    // Named "subTab" because "tab" breaks the navigation.
    const selectedTab: string = this.route?.snapshot?.queryParams['subTab'];

    if (!isNullOrUndefined(selectedTab)) {
      this.handleSelectedTabChange(selectedTab);
    }
  }

  handleEditUzurvRider(): void {
    this.store$.dispatch(OrganizationRiderActions.OpenEditUzurvRiderDetail());
  }

  handleEditOrganizationRider(data): void {
    this.store$.dispatch(
      OrganizationRiderActions.OpenEditOrganizationRiderDetail({
        payload: { organizationRiderDetail: data[0], organizationServiceLevels: data[2], organizationRiderPools: data[3] }
      })
    );
  }

  handleAddOrganizationRider(): void {
    this.store$.dispatch(OrganizationRiderActions.OpenAddOrganizationRiderDialog());
  }

  handleSubmitPaymentForOutstandingCharges(chargePaymentData: any): void {
    this.store$.dispatch(
      OrganizationRiderActions.SubmitPaymentForOutstandingCharges({
        payload: {
          selectedPaymentAccount: chargePaymentData.selectedPaymentAccount,
          selectedCharges: chargePaymentData.selectedCharges
        }
      })
    );
  }

  handleDeleteFavoriteDriver(driver: Driver): void {
    this.store$.dispatch(OrganizationRiderActions.RemoveFavoriteDriver({ driver }));
  }

  handleDeleteIgnoredDriver(driver: Driver): void {
    this.store$.dispatch(OrganizationRiderActions.RemoveIgnoredDriver({ driver }));
  }

  handleAddPaymentAccount(): void {
    this.dialog.open(OrganizationRiderPaymentAccountDialog, {
      autoFocus: false,
      width: '50em',
      maxWidth: '50em',
      height: '55em',
      hasBackdrop: true,
      data: {
        paymentAccount: new PaymentAccount(),
        isEditMode: false
      }
    });
  }

  handleAddPaymentAccountSuccess(createdPaymentAccount: PaymentAccount): void {
    this.store$.dispatch(OrganizationRiderActions.AddPaymentAccountSuccess({ paymentAccount: createdPaymentAccount }));
  }

  handleCancelAddPaymentAccount(): void {
    this.store$.dispatch(OrganizationRiderActions.CancelAddPaymentAccount());
  }

  handleOpenEditPaymentAccount(paymentAccount: PaymentAccount): void {
    this.dialog.open(OrganizationRiderPaymentAccountDialog, {
      autoFocus: false,
      width: '50em',
      maxWidth: '50em',
      height: '55em',
      hasBackdrop: true,
      data: {
        paymentAccount: cloneDeep(paymentAccount),
        isEditMode: true
      }
    });
  }

  handlePagePaymentsList(pageEvent: PageEvent) {
    this.store$.dispatch(OrganizationRiderActions.PagePaymentsList({ pageEvent }));
  }

  handleOpenNewPaymentDialog(): void {
    this.store$.dispatch(OrganizationRiderActions.OpenNewPaymentDialog());
  }

  handleExpandPayment(payment: Payment): void {
    this.store$.dispatch(OrganizationRiderActions.ExpandPayment({ payment }));
  }

  handleVoidPayment(payment: Payment): void {
    this.store$.dispatch(OrganizationRiderActions.VoidPayment({ payment }));
  }

  handleResubmitPayment(payment: Payment): void {
    this.store$.dispatch(OrganizationRiderActions.ResubmitPayment({ payment }));
  }

  handleOpenRefundDialog(payment: Payment): void {
    this.store$.dispatch(OrganizationRiderActions.OpenRefundDialog({ payment }));
  }

  handleAddNote(note: CustomerServiceNote): void {
    this.store$.dispatch(OrganizationRiderActions.AddNote({ payload: note }));
  }

  handleSendUzurvRideAppInvitation(rider: OrganizationRiderDetail) {
    this.store$.dispatch(
      OrganizationRiderActions.ConfirmSendUzurvRideAppInvitation({ organizationRiderDetail: rider, fromNewItinerary: false })
    );
  }

  handleEligibilityToggle(organizationRiderDetail: OrganizationRiderDetail, isRiderEligible: boolean): void {
    this.store$.dispatch(OrganizationRiderActions.UpdateOrganizationRiderEligibility({ organizationRiderDetail, isRiderEligible }));
  }

  handleTripsPaginate(pageEvent: PageEvent): void {
    this.store$.dispatch(OrganizationRiderActions.TripsPagination({ pageEvent }));
  }

  handleGoToTrip(trip: Trip): void {
    this.store$.dispatch(OpenTripDetailTab({ payload: { trip: trip } }));
  }

  handleSaveMobilityProfile(mobilityProfileDetail: MobilityProfileDetail): void {
    if (mobilityProfileDetail.verificationNeeded) {
      this.store$.dispatch(OrganizationRiderActions.VerifyRiderMobilityProfile({ mobilityProfileDetail }));
    } else {
      this.store$.dispatch(OrganizationRiderActions.UpdateRiderMobilityProfile({ mobilityProfileDetail }));
    }
  }

  handleSelectedTabChange(tabLabel: string) {
    this.store$.dispatch(
      OrganizationRiderActions.SelectRiderDetailTab({
        selectedRiderDetailTab: tabLabel as RiderDetailTabs
      })
    );
  }

  handleCreateRiderSuspensionClicked() {
    this.store$.dispatch(
      OrganizationRiderActions.CreateRiderSuspension({
        rider: this.rider
      })
    );
  }

  canViewRiderSuspension(): boolean {
    return this.authDataService.hasPermission({
      any: [PermissionsConstants.RIDER_SUSPENSION_READ],
      all: []
    });
  }

  canCreateRiderSuspension(): boolean {
    return this.authDataService.hasPermission({
      any: [PermissionsConstants.RIDER_SUSPENSION_CREATE],
      all: []
    });
  }

  canRemovePermanentRiderSuspension(): boolean {
    return this.authDataService.hasPermission({
      any: [PermissionsConstants.RIDER_SUSPENSION_PERMANENT_DELETE],
      all: []
    });
  }

  canRemoveTemporaryRiderSuspension(): boolean {
    return this.authDataService.hasPermission({
      any: [PermissionsConstants.RIDER_SUSPENSION_DELETE],
      all: []
    });
  }

  handleRemoveRiderSuspensionClicked(suspension: PersonSuspensionDetail) {
    this.store$.dispatch(
      OrganizationRiderActions.RemoveRiderSuspension({
        riderId: this.rider.id,
        riderSuspensionId: suspension.id
      })
    );
  }

  getTabbingConfig(): Tab<OrganizationRiderState> {
    return {
      name: 'Rider',
      svgIcon: 'uz_rider',
      disableClose: false,
      selectorState: OrganizationRiderSelectors.organizationRiderState,
      initializeActionOnCacheLoaded: true,
      initializeAction: tabConfig =>
        OrganizationRiderActions.GetOrganizationRiderDetail({
          payload: { organizationId: +tabConfig.routeParams['organizationId'], organizationRiderId: +tabConfig.routeParams['riderId'] }
        }),
      activatedAction: tabConfig =>
        OrganizationRiderActions.GetOrganizationRiderDetail({
          payload: { organizationId: +tabConfig.routeParams['organizationId'], organizationRiderId: +tabConfig.routeParams['riderId'] }
        }),
      restoreSnapshotAction: snapshot => OrganizationRiderActions.RestoreState({ state: snapshot }),
      closeTabAction: () => OrganizationRiderActions.ClearState()
    };
  }
}
